import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import type { FilterGroup } from '../model/types';

interface FilterGroupHeaderProps {
  group: FilterGroup;
  selectedCount: number;
  onSelectAll: () => void;
  onClear: () => void;
  showSelectAll?: boolean;
}

/**
 * The group's own name row, carrying the same pair of actions the header offers
 * project-wide. Replaces `CommandGroup`'s `heading`, which cmdk renders
 * `aria-hidden` — buttons inside it would be unreachable for assistive tech.
 *
 * Sticks to the top of the list while its group scrolls under it: cmdk renders it inside the group's own items
 * wrapper, so it leaves with the group's last item and the next group's header takes its place.
 */
export function FilterGroupHeader({
  group,
  selectedCount,
  onSelectAll,
  onClear,
  showSelectAll = true
}: FilterGroupHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-popover px-2 py-1.5">
      <span className="text-xs font-medium text-muted-foreground">{group.group}</span>
      <div className="flex gap-1">
        {showSelectAll && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onSelectAll}
            disabled={selectedCount === group.items.length}
            aria-label={`${t('gen.select.all.label')} — ${group.group}`}
            className="h-7 px-2 text-xs">
            {t('gen.select.all.label')}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          disabled={selectedCount === 0}
          aria-label={`${t('common.clear')} — ${group.group}`}
          className="h-7 px-2 text-xs">
          {t('common.clear')}
        </Button>
      </div>
    </div>
  );
}
